import { useEffect, useState } from "react";
import { useUser } from "../context/UserContext";
import {
  checkVAPermissionCreateVA,
  checkVAPermissionCreateBrainBank,
} from "../service/apiService";

/**
 * Whether the current user may create Virtual Assistants and BrainBanks.
 *
 * Admins always may. Regular users follow the two toggles under Settings →
 * User Permissions. Cosmetic only -- it hides Create buttons; the API enforces
 * the same rule on its own.
 *
 * Used by list views and by every chat message, so the check is shared: one
 * request pair per minute however many components ask.
 */
export interface CreatePermissions {
  canCreateVA: boolean;
  canCreateBrainBank: boolean;
}

const ALLOW_ALL: CreatePermissions = { canCreateVA: true, canCreateBrainBank: true };
// While the check is in flight: hidden, not allowed.
const PENDING: CreatePermissions = { canCreateVA: false, canCreateBrainBank: false };
const CACHE_MS = 60_000;

let cached: { value: CreatePermissions; at: number } | null = null;
let inflight: Promise<CreatePermissions> | null = null;

const loadCreatePermissions = (): Promise<CreatePermissions> => {
  if (cached && Date.now() - cached.at < CACHE_MS) return Promise.resolve(cached.value);
  if (!inflight) {
    inflight = Promise.all([checkVAPermissionCreateVA(), checkVAPermissionCreateBrainBank()])
      .then(([canCreateVA, canCreateBrainBank]) => {
        const value = { canCreateVA, canCreateBrainBank };
        cached = { value, at: Date.now() };
        return value;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

export const useCreatePermissions = (): CreatePermissions => {
  const { userDetails } = useUser();
  const isAdmin = userDetails?.role === "admin";
  const userKnown = Boolean(userDetails);
  // Start from the last known answer; with none yet, keep the buttons hidden
  // until the check returns, so a blocked user never sees one flash and vanish.
  const [permissions, setPermissions] = useState<CreatePermissions | null>(
    cached?.value ?? null
  );

  useEffect(() => {
    if (isAdmin || !userKnown) return;
    let active = true;
    loadCreatePermissions().then(value => {
      if (active) setPermissions(value);
    });
    return () => {
      active = false;
    };
  }, [isAdmin, userKnown]);

  if (isAdmin) return ALLOW_ALL;
  return permissions ?? PENDING;
};

export default useCreatePermissions;
